import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Loader2, Search, Shield, X, Plus, Mail, Building2, Filter, Users, ClipboardCheck, CheckCircle, XCircle, RotateCcw, Globe, Phone, Briefcase, GraduationCap, Calendar, Edit, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { displayName } from '@/lib/utils';
import { TECHNOLOGY_AREA_LABEL_MAP, ATTENDEE_PARTICIPANT_TYPES, TECHNOLOGY_AREAS, ATTENDEE_ACKNOWLEDGEMENTS } from '@/lib/constants';
import { getApiErrorMessage } from '@/lib/apiError';
import MilitaryFields from '@/components/registration/MilitaryFields';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminEventFilter } from '@/hooks/useAdminEventFilter';
import { formatEventDateRange } from '@/hooks/useActiveEvent';
import { toast } from 'sonner';
import { DesktopShell } from '@/components/desktop/DesktopShell';
import { ThreePanelLayout } from '@/components/desktop/layouts/ThreePanelLayout';
import type { Registration, RegistrationStatus, ProjectStatus } from '@/types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

interface RegistrationsResponse {
  success: boolean;
  data: Registration[];
  counts?: Record<string, number>;
  pagination?: { total: number; limit: number; offset: number };
}

const STATUS_STYLES: Record<RegistrationStatus, { color: string; bg: string; label: string }> = {
  pending: { color: '#ca8a04', bg: 'bg-yellow-50 dark:bg-yellow-950', label: 'Pending' },
  approved: { color: '#16a34a', bg: 'bg-green-50 dark:bg-green-950', label: 'Approved' },
  rejected: { color: '#dc2626', bg: 'bg-red-50 dark:bg-red-950', label: 'Rejected' },
};

const ORG_LABELS: Record<string, string> = {
  nps: 'NPS',
  government: 'Government',
  industry: 'Industry',
};

const PARTICIPANT_TYPE_FILTERS = [
  { value: 'student', label: 'NPS Student' },
  { value: 'nps_staff', label: 'NPS Faculty/Staff' },
  { value: 'alumni', label: 'NPS Alumni' },
  { value: 'industry', label: 'Industry' },
  { value: 'active_reserve_military', label: 'Active/Reserve Military' },
  { value: 'government_employee', label: 'Government Employee' },
  { value: 'other', label: 'Other' },
];

type StatusFilterValue = RegistrationStatus | 'all' | 'at_event';

const STATUS_FILTER_OPTIONS: { value: StatusFilterValue; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'at_event', label: 'At Event' },
  { value: 'rejected', label: 'Rejected' },
];

export default function UserManagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Registrations state
  const [regLoading, setRegLoading] = useState(false);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [regSearch, setRegSearch] = useState('');
  const [regStatusFilter, setRegStatusFilter] = useState<StatusFilterValue>('all');
  const [selectedParticipantType, setSelectedParticipantType] = useState<string>('all');
  const [selectedReg, setSelectedReg] = useState<Registration | null>(null);
  const { eventFilter, setEventFilter, activeEventId, events, fetchEvents, getResolvedEventId } = useAdminEventFilter();

  // Vetting dialogs
  const [approveDialog, setApproveDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [reinstateDialog, setReinstateDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [preservePoc, setPreservePoc] = useState(false);

  // New Registration modal
  const [newRegDialog, setNewRegDialog] = useState(false);
  const [newRegLoading, setNewRegLoading] = useState(false);
  const emptyRegForm = {
    firstName: '',
    lastName: '',
    email: '',
    participantType: '',
    rank: '',
    branchOfService: '',
    isRetired: false,
    organization: '',
    role: '',
    linkedinUrl: '',
    websiteUrl: '',
    bio: '',
    accelerationInterests: [] as string[],
  };
  const [newRegForm, setNewRegForm] = useState({ ...emptyRegForm });
  // After an admin-created registration, remind them to collect the attendee's
  // acknowledgements (which the admin can't attest on their behalf).
  const [ackReminder, setAckReminder] = useState<{ name: string; email: string } | null>(null);

  // Edit modal
  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ ...emptyRegForm });
  const [_editRegId, setEditRegId] = useState<string | null>(null);
  const [editProfileId, setEditProfileId] = useState<string | null>(null);
  const [editRoles, setEditRoles] = useState<{ isAdmin: boolean; isStaff: boolean }>({ isAdmin: false, isStaff: false });
  const [editRolesOriginal, setEditRolesOriginal] = useState<{ isAdmin: boolean; isStaff: boolean }>({ isAdmin: false, isStaff: false });
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    if (!user) {
      toast.error('You must be logged in');
      navigate('/auth');
      return;
    }
    if (!user.roles?.includes('admin')) {
      toast.error('Access denied. Admin privileges required.');
      navigate('/');
      return;
    }
    setIsAdmin(true);
    setCheckingAuth(false);
    fetchActiveEvent();
  };

  const fetchActiveEvent = async () => {
    await fetchEvents();
    fetchRegistrations();
  };

  const fetchRegistrations = useCallback(async () => {
    setRegLoading(true);
    try {
      // Load the full list (no pagination) so every registration is searchable
      // client-side — mirrors the user-facing Network page (ConnectionsPage).
      const params = new URLSearchParams({ limit: '10000' });
      const eventId = getResolvedEventId();
      if (eventId) params.set('eventId', eventId);

      const res = await api.get<RegistrationsResponse>(`/admin/registrations?${params}`);
      if (res.success) {
        setRegistrations(res.data);
        if (selectedReg) {
          const updated = res.data.find((r) => r.id === selectedReg.id);
          if (updated) setSelectedReg(updated);
          else setSelectedReg(null);
        }
      }
    } catch {
      toast.error('Failed to load registrations');
    } finally {
      setRegLoading(false);
    }
  }, [selectedReg]); // eventFilter and activeEventId read from refs via getResolvedEventId

  // Refetch only when event filter changes (skip initial mount — fetchActiveEvent handles that)
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (isAdmin) {
      fetchRegistrations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventFilter]);

  // Client-side filtering for status, at_event, search, and participant type
  const filteredRegistrations = useMemo(() => {
    return registrations.filter((reg) => {
      // Status filter
      if (regStatusFilter === 'at_event') {
        if (!reg.profile?.isCheckedIn) return false;
      } else if (regStatusFilter !== 'all') {
        if (reg.status !== regStatusFilter) return false;
      }
      // Search filter
      if (regSearch.trim()) {
        const q = regSearch.toLowerCase();
        if (
          !displayName(reg).toLowerCase().includes(q) &&
          !reg.email.toLowerCase().includes(q)
        ) return false;
      }
      // Participant type filter
      if (selectedParticipantType !== 'all') {
        if (!reg.participantType || reg.participantType !== selectedParticipantType) {
          return false;
        }
      }
      return true;
    });
  }, [registrations, regStatusFilter, regSearch, selectedParticipantType]);

  // Clear selection when it's no longer in the visible list
  useEffect(() => {
    if (selectedReg && !filteredRegistrations.some((r) => r.id === selectedReg.id)) {
      setSelectedReg(null);
    }
  }, [filteredRegistrations, selectedReg]);

  const filterCount = (regSearch ? 1 : 0) + (regStatusFilter !== 'all' ? 1 : 0) + (eventFilter !== 'active' ? 1 : 0) + (selectedParticipantType !== 'all' ? 1 : 0);

  // Derive visible selection — clears instantly when item leaves filtered list (no useEffect delay)
  const visibleReg = selectedReg && filteredRegistrations.some((r) => r.id === selectedReg.id) ? selectedReg : null;

  const clearFilters = () => {
    setRegSearch('');
    setRegStatusFilter('all');
    setEventFilter('active');
    setSelectedParticipantType('all');
  };

  const handleApprove = async () => {
    if (!selectedReg) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/registrations/${selectedReg.id}/approve`, {});
      toast.success(`${displayName(selectedReg)} approved`);
      setApproveDialog(false);
      setSelectedReg(null);
      await fetchRegistrations();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to approve');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedReg) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/registrations/${selectedReg.id}/reject`, {
        preservePocAsAttendee: preservePoc,
      });
      toast.success(
        preservePoc
          ? `Exhibitor rejected — ${displayName(selectedReg)} approved as attendee`
          : `${displayName(selectedReg)} rejected`
      );
      setRejectDialog(false);
      setPreservePoc(false);
      setSelectedReg(null);
      await fetchRegistrations();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to reject');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReinstate = async () => {
    if (!selectedReg) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/registrations/${selectedReg.id}/reinstate`, {});
      toast.success(`${displayName(selectedReg)} reinstated to pending`);
      setReinstateDialog(false);
      await fetchRegistrations();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to reinstate');
    } finally {
      setActionLoading(false);
    }
  };

  const handleNewRegistration = async () => {
    if (!activeEventId) {
      toast.error('No active event found. Cannot create registration.');
      return;
    }
    if (!newRegForm.firstName.trim() || !newRegForm.lastName.trim() || !newRegForm.email.trim()) {
      toast.error('First name, last name, and email are required.');
      return;
    }
    setNewRegLoading(true);
    try {
      await api.post('/register', {
        registrationType: 'attendee',
        eventId: activeEventId,
        firstName: newRegForm.firstName.trim(),
        lastName: newRegForm.lastName.trim(),
        email: newRegForm.email.trim(),
        participantType: newRegForm.participantType || undefined,
        rank: newRegForm.rank.trim() || undefined,
        branchOfService: newRegForm.branchOfService.trim() || undefined,
        isRetired: newRegForm.isRetired,
        organization: newRegForm.organization.trim() || undefined,
        role: newRegForm.role.trim() || undefined,
        linkedinUrl: newRegForm.linkedinUrl.trim() || undefined,
        websiteUrl: newRegForm.websiteUrl.trim() || undefined,
        bio: newRegForm.bio.trim() || undefined,
        accelerationInterests: newRegForm.accelerationInterests.length > 0 ? newRegForm.accelerationInterests : undefined,
        acknowledgements: [],
      });
      toast.success('Registration created successfully');
      const created = { name: `${newRegForm.firstName.trim()} ${newRegForm.lastName.trim()}`.trim(), email: newRegForm.email.trim() };
      setNewRegDialog(false);
      setNewRegForm({ ...emptyRegForm });
      await fetchRegistrations();
      // Admin-created registrations have no acknowledgements — prompt to follow up.
      setAckReminder(created);
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Failed to create registration'));
    } finally {
      setNewRegLoading(false);
    }
  };

  const openEditDialog = async (reg: Registration) => {
    setEditForm({
      firstName: reg.firstName,
      lastName: reg.lastName,
      email: reg.email,
      participantType: reg.participantType || '',
      rank: reg.rank || '',
      branchOfService: reg.branchOfService || '',
      isRetired: reg.isRetired || false,
      organization: reg.organization || '',
      role: reg.role || '',
      linkedinUrl: reg.linkedinUrl || '',
      websiteUrl: reg.websiteUrl || '',
      bio: reg.bio || '',
      accelerationInterests: reg.accelerationInterests || [],
    });
    setEditRegId(reg.id);
    setEditProfileId(reg.id);

    // Fetch current roles if user has a profile (approved)
    const defaultRoles = { isAdmin: false, isStaff: false };
    if (reg.profileId) {
      try {
        const res = await api.get<{ success: boolean; data: { id: string; userRoles?: { role: string }[] } }>(`/admin/users/${reg.profileId}`);
        if (res.success && res.data?.userRoles) {
          const roles = res.data.userRoles.map((r) => r.role);
          defaultRoles.isAdmin = roles.includes('admin');
          defaultRoles.isStaff = roles.includes('staff');
        }
      } catch {
        // Non-critical — just show unchecked
      }
    }
    setEditRoles(defaultRoles);
    setEditRolesOriginal(defaultRoles);
    setEditDialog(true);
  };

  const handleEditSave = async () => {
    if (!editProfileId) {
      toast.info('Role changes require an approved registration with a profile.');
      setEditDialog(false);
      return;
    }
    setEditSaving(true);
    try {
      // Save profile field changes
      await api.put(`/admin/users/${editProfileId}`, {
        firstName: editForm.firstName.trim(),
        lastName: editForm.lastName.trim(),
        participantType: editForm.participantType || null,
        rank: editForm.rank || null,
        branchOfService: editForm.branchOfService || null,
        isRetired: editForm.isRetired,
        organization: editForm.organization || null,
        role: editForm.role || null,
        linkedinUrl: editForm.linkedinUrl || null,
        websiteUrl: editForm.websiteUrl || null,
        bio: editForm.bio || null,
        accelerationInterests: editForm.accelerationInterests,
      });
      // Handle admin role changes
      if (editRoles.isAdmin && !editRolesOriginal.isAdmin) {
        await api.post(`/admin/users/${editProfileId}/roles`, { role: 'admin' });
      } else if (!editRoles.isAdmin && editRolesOriginal.isAdmin) {
        await api.delete(`/admin/users/${editProfileId}/roles/admin`);
      }
      // Handle staff role changes
      if (editRoles.isStaff && !editRolesOriginal.isStaff) {
        await api.post(`/admin/users/${editProfileId}/roles`, { role: 'staff' });
      } else if (!editRoles.isStaff && editRolesOriginal.isStaff) {
        await api.delete(`/admin/users/${editProfileId}/roles/staff`);
      }
      toast.success('Changes saved');
      setEditDialog(false);
      await fetchRegistrations();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to save changes');
    } finally {
      setEditSaving(false);
    }
  };

  const getInitials = (name: string) =>
    name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) return null;

  const CardSkeleton = () => (
    <div className="flex items-center gap-3 px-6 py-3 border-b border-border">
      <Skeleton className="w-9 h-9 rounded-full flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <Skeleton className="h-4 w-32 mb-1" />
        <Skeleton className="h-3 w-48" />
      </div>
    </div>
  );

  // =====================
  // Column 1: Filters
  // =====================

  const FiltersPanel = (
    <div className="h-full flex flex-col">
      <div className="h-[72px] px-4 flex items-center justify-between border-b border-border bg-muted flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Filter className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Filters</h2>
            <p className="text-sm text-muted-foreground">Refine your search</p>
          </div>
        </div>
        {filterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-7 text-[11px] px-2">
            Clear ({filterCount})
          </Button>
        )}
      </div>
      <ScrollArea className="flex-1">
        <div className="px-3 py-3 space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              placeholder="Name or email..."
              value={regSearch}
              onChange={(e) => setRegSearch(e.target.value)}
              className="pl-8 h-8 text-xs"
            />
            {regSearch && (
              <Button variant="ghost" size="icon" className="absolute right-0.5 top-1/2 -translate-y-1/2 h-6 w-6" onClick={() => setRegSearch('')}>
                <X className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>

          <Separator />

          {/* Status Filter */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Status</Label>
            <RadioGroup value={regStatusFilter} onValueChange={(v) => setRegStatusFilter(v as StatusFilterValue)} className="gap-0.5">
              {STATUS_FILTER_OPTIONS.map(({ value, label }) => (
                <div key={value} className="flex items-center gap-1.5 py-0.5">
                  <RadioGroupItem value={value} id={`status-${value}`} className="h-3.5 w-3.5" />
                  <label htmlFor={`status-${value}`} className="text-xs cursor-pointer">{label}</label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <Separator />

          {/* Event Filter */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Event</Label>
            <RadioGroup value={eventFilter} onValueChange={(v) => { setEventFilter(v); setSelectedReg(null); }} className="gap-0.5">
              <div className="flex items-center gap-1.5 py-0.5">
                <RadioGroupItem value="all" id="event-all" className="h-3.5 w-3.5" />
                <label htmlFor="event-all" className="text-xs cursor-pointer">All Events</label>
              </div>
              <div className="flex items-center gap-1.5 py-0.5">
                <RadioGroupItem value="active" id="event-active" className="h-3.5 w-3.5" />
                <label htmlFor="event-active" className="text-xs cursor-pointer">Active Event</label>
              </div>
              {events
                .filter((ev) => ev.status === 'archived')
                .sort((a, b) => new Date(b.dateStart).getTime() - new Date(a.dateStart).getTime())
                .map((ev) => (
                  <div key={ev.id} className="flex items-center gap-1.5 py-0.5">
                    <RadioGroupItem value={ev.id} id={`event-${ev.id}`} className="h-3.5 w-3.5" />
                    <label htmlFor={`event-${ev.id}`} className="text-xs cursor-pointer flex items-center justify-between w-full">
                      <span className="truncate">{ev.name}</span>
                      <span className="text-[10px] text-muted-foreground shrink-0 ml-1">
                        {new Date(ev.dateStart).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                      </span>
                    </label>
                  </div>
                ))}
            </RadioGroup>
          </div>

          <Separator />

          {/* Participant Type Filter */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Participant Type</Label>
            <RadioGroup value={selectedParticipantType} onValueChange={setSelectedParticipantType} className="gap-0.5">
              <div className="flex items-center gap-1.5 py-0.5">
                <RadioGroupItem value="all" id="ptype-all" className="h-3.5 w-3.5" />
                <label htmlFor="ptype-all" className="text-xs cursor-pointer">All</label>
              </div>
              {PARTICIPANT_TYPE_FILTERS.map(({ value, label }) => (
                <div key={value} className="flex items-center gap-1.5 py-0.5">
                  <RadioGroupItem value={value} id={`ptype-${value}`} className="h-3.5 w-3.5" />
                  <label htmlFor={`ptype-${value}`} className="text-xs cursor-pointer">{label}</label>
                </div>
              ))}
            </RadioGroup>
          </div>
        </div>
      </ScrollArea>
    </div>
  );

  // =====================
  // Column 2: Community
  // =====================

  const CommunityPanel = (
    <div className="h-full flex flex-col">
      <div className="h-[72px] px-4 flex items-center justify-between border-b border-border bg-muted flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Users className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Community</h2>
            <p className="text-sm text-muted-foreground">
              {regLoading
                ? 'Loading...'
                : filteredRegistrations.length === registrations.length
                  ? `${registrations.length} registrations`
                  : `${filteredRegistrations.length} of ${registrations.length} registrations`}
            </p>
          </div>
        </div>
        <Button size="sm" className="h-8 gap-1.5 text-xs" onClick={() => { setNewRegForm({ ...emptyRegForm }); setNewRegDialog(true); }}>
          <Plus className="h-3.5 w-3.5" /> New Registration
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {regLoading ? (
          Array.from({ length: 8 }).map((_, i) => <CardSkeleton key={i} />)
        ) : filteredRegistrations.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="font-medium">No registrations found</p>
            <p className="text-sm mt-1">Try adjusting your filters</p>
          </div>
        ) : (
          filteredRegistrations.map((reg) => (
            <div
              key={reg.id}
              className={`w-full flex items-center gap-3 px-6 py-3 border-b border-border text-left transition-colors hover:bg-muted/50 ${
                selectedReg?.id === reg.id ? 'bg-primary/5 border-l-2 border-l-primary' : ''
              }`}
            >
              <button
                className="flex items-center gap-3 flex-1 min-w-0 text-left"
                onClick={() => setSelectedReg(reg)}
              >
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-900 to-blue-700 flex items-center justify-center text-white font-semibold flex-shrink-0 text-xs">
                  {getInitials(displayName(reg))}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{displayName(reg)}</p>
                  <p className="text-xs text-muted-foreground truncate">{reg.email}</p>
                  {reg.organization && (
                    <p className="text-xs text-muted-foreground truncate">{reg.organization}</p>
                  )}
                </div>
              </button>
              {reg.profile?.isCheckedIn && (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0"
                  style={{ backgroundColor: '#7c3aed15', color: '#7c3aed' }}
                >
                  At Event
                </span>
              )}
              {reg.acknowledgementsPending && (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 flex items-center gap-1"
                  style={{ backgroundColor: '#f59e0b15', color: '#b45309' }}
                  title="Admin-created — attendee has not accepted the required acknowledgements"
                >
                  <AlertCircle className="h-3 w-3" /> Acks pending
                </span>
              )}
              {reg.status === 'approved' && reg.eventId && reg.partner?.exhibitorEventIds?.includes(reg.eventId) ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 bg-teal-600 text-white">
                  Exhibitor
                </span>
              ) : (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0"
                  style={{
                    backgroundColor: `${STATUS_STYLES[reg.status].color}15`,
                    color: STATUS_STYLES[reg.status].color,
                  }}
                >
                  {STATUS_STYLES[reg.status].label}
                </span>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 flex-shrink-0 text-muted-foreground hover:text-foreground"
                onClick={(e) => { e.stopPropagation(); openEditDialog(reg); }}
              >
                <Edit className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );

  // =====================
  // Column 3: Registration Details
  // =====================
  const sel = visibleReg;

  const DetailsPanel = (
    <div className="h-full flex flex-col">
      <div className="h-[72px] px-4 flex items-center justify-between border-b border-border bg-muted flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0 flex-1 mr-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Shield className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h2 className="font-semibold text-lg">Registration Details</h2>
            <p className="text-sm text-muted-foreground truncate">{sel ? displayName(sel) : 'Select a registration'}</p>
          </div>
        </div>
        {sel && sel.status === 'pending' && (
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-700 h-8 text-xs"
              onClick={() => setApproveDialog(true)}
              disabled={actionLoading}
            >
              {actionLoading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5 mr-1.5" />}
              Approve
            </Button>
            <Button
              size="sm"
              variant="destructive"
              className="h-8 text-xs"
              onClick={() => setRejectDialog(true)}
            >
              <XCircle className="h-3.5 w-3.5 mr-1.5" />
              Reject
            </Button>
          </div>
        )}
        {sel && sel.status === 'rejected' && (
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs border-amber-500 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950"
              onClick={() => setReinstateDialog(true)}
              disabled={actionLoading}
            >
              {actionLoading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 mr-1.5" />}
              Reinstate
            </Button>
          </div>
        )}
      </div>
      <ScrollArea className="flex-1">
        {sel ? (
          <div className="p-6 space-y-5">
            {/* Header */}
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-900 to-blue-700 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                {getInitials(displayName(sel))}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold truncate">{displayName(sel)}</h3>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <Badge variant="outline" className="text-xs" style={{ borderColor: STATUS_STYLES[sel.status].color, color: STATUS_STYLES[sel.status].color }}>
                    {STATUS_STYLES[sel.status].label}
                  </Badge>
                  {/* Exhibitor designation — when the linked Partner is approved as an
                      Exhibitor (has a booth) for this event. Same teal pill used on the
                      public Network page + admin Partners list. */}
                  {sel.eventId && sel.partner?.exhibitorEventIds?.includes(sel.eventId) && (
                    <Badge variant="default" className="text-xs bg-teal-600 hover:bg-teal-700 text-white border-transparent">
                      Exhibitor
                    </Badge>
                  )}
                  {sel.profile?.userRoles?.some(r => r.role === 'admin') && (
                    <Badge variant="outline" className="text-xs" style={{ borderColor: '#ef4444', color: '#ef4444' }}>Admin</Badge>
                  )}
                  {sel.profile?.userRoles?.some(r => r.role === 'staff') && (
                    <Badge variant="outline" className="text-xs" style={{ borderColor: '#8b5cf6', color: '#8b5cf6' }}>Staff</Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Returning Attendee Info */}
            {sel.isReturningAttendee && (
              <Card className="p-4 bg-blue-50 border-blue-200">
                <p className="text-xs font-semibold text-blue-700 mb-2">Returning Attendee</p>
                <p className="text-sm text-blue-900">This registrant has an existing account and has previously attended events.</p>
                {(sel.previousEvents?.length ?? 0) > 0 && (
                  <div className="mt-2 space-y-1">
                    {sel.previousEvents!.map((pe: { eventName: string }, i: number) => (
                      <p key={i} className="text-xs text-blue-700">Previously approved for <strong>{pe.eventName}</strong></p>
                    ))}
                  </div>
                )}
              </Card>
            )}

            {/* Exhibitor Rejected — POC Preserved as Attendee */}
            {sel.exhibitorRejectedPocPreserved && (
              <Card className="p-4 bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800">
                <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1">Exhibitor Application Rejected</p>
                <p className="text-sm text-amber-900 dark:text-amber-300">
                  {sel.partner?.name ?? sel.organization ?? 'The organization'}'s exhibitor application was rejected, but {sel.firstName} was approved as a general attendee.
                </p>
              </Card>
            )}

            {/* Contact Info */}
            <Card className="p-4 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground mb-2">Contact</p>
              <DetailRow icon={<Mail className="h-3.5 w-3.5" />} value={sel.email} />
              {sel.phone && <DetailRow icon={<Phone className="h-3.5 w-3.5" />} value={sel.phone} />}
              {sel.linkedinUrl && <DetailRow icon={<Globe className="h-3.5 w-3.5" />} value={sel.linkedinUrl} link />}
              {sel.websiteUrl && <DetailRow icon={<Globe className="h-3.5 w-3.5" />} value={sel.websiteUrl} link />}
            </Card>

            {/* Organization Info */}
            <Card className="p-4 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground mb-2">Organization</p>
              {sel.organizationType && <DetailRow icon={<Building2 className="h-3.5 w-3.5" />} label="Type" value={ORG_LABELS[sel.organizationType] || sel.organizationType} />}
              {sel.organization && <DetailRow icon={<Building2 className="h-3.5 w-3.5" />} label="Organization" value={sel.organization} />}
              {sel.participantType && <DetailRow icon={<GraduationCap className="h-3.5 w-3.5" />} label="Participant Type" value={ATTENDEE_PARTICIPANT_TYPES.find((t) => t.value === sel.participantType)?.label ?? sel.participantType} />}
              {sel.department && <DetailRow icon={<Briefcase className="h-3.5 w-3.5" />} label="Department" value={sel.department} />}
              {sel.rank && <DetailRow label="Rank" value={sel.rank} />}
              {sel.branchOfService && <DetailRow label="Branch" value={sel.branchOfService} />}
              {sel.role && <DetailRow label="Role" value={sel.role} />}
            </Card>

            {/* Partner Organization */}
            {sel.partner && (
              <Card className="p-4 space-y-2">
                <p className="text-xs font-semibold text-muted-foreground mb-2">Partner Organization</p>
                <div className="flex items-center gap-2">
                  <Building2 className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm">{sel.partner.name}</span>
                  {sel.partner.isFeatured && (
                    <Badge variant="secondary" className="text-[10px]">Featured</Badge>
                  )}
                </div>
              </Card>
            )}

            {/* Exhibitor-Specific Fields */}
            {sel.registrationType === 'exhibitor' && (sel.exhibitorOrgType || sel.workshopInterest || sel.workshopDescription) && (
              <Card className="p-4 space-y-2">
                <p className="text-xs font-semibold text-muted-foreground mb-2">Exhibitor Details</p>
                {sel.exhibitorOrgType && <DetailRow label="Org Type" value={sel.exhibitorOrgType} />}
                {sel.workshopInterest !== undefined && <DetailRow label="Workshop Interest" value={sel.workshopInterest ? 'Yes' : 'No'} />}
                {sel.workshopDescription && <DetailRow label="Workshop Description" value={sel.workshopDescription} />}
              </Card>
            )}

            {/* NPS Project-Specific Fields */}
            {sel.registrationType === 'nps_project' && (sel.projectedGraduation || sel.projectPitchInterest !== undefined || sel.showcaseTableInterest !== undefined) && (
              <Card className="p-4 space-y-2">
                <p className="text-xs font-semibold text-muted-foreground mb-2">NPS Project Details</p>
                {sel.projectedGraduation && <DetailRow icon={<GraduationCap className="h-3.5 w-3.5" />} label="Projected Graduation" value={sel.projectedGraduation} />}
                {sel.projectPitchInterest !== undefined && <DetailRow label="Project Pitch Interest" value={sel.projectPitchInterest ? 'Yes' : 'No'} />}
                {sel.showcaseTableInterest !== undefined && <DetailRow label="Showcase Table Interest" value={sel.showcaseTableInterest ? 'Yes' : 'No'} />}
              </Card>
            )}


            {/* Bio */}
            {sel.bio && (
              <Card className="p-4">
                <p className="text-xs font-semibold text-muted-foreground mb-2">Bio</p>
                <p className="text-sm">{sel.bio}</p>
              </Card>
            )}

            {/* Technology Interests */}
            {sel.accelerationInterests && sel.accelerationInterests.length > 0 && (
              <Card className="p-4">
                <p className="text-xs font-semibold text-muted-foreground mb-2">Technology Interests</p>
                <div className="flex flex-wrap gap-1">
                  {sel.accelerationInterests.filter((interest: string) => interest && (TECHNOLOGY_AREA_LABEL_MAP[interest] || interest.trim())).map((interest: string) => (
                    <Badge key={interest} variant="secondary" className="text-xs">{TECHNOLOGY_AREA_LABEL_MAP[interest] || interest}</Badge>
                  ))}
                </div>
              </Card>
            )}

            {/* Projects */}
            {sel.projectData && Array.isArray(sel.projectData) && sel.projectData.length > 0 && (
              <div className="space-y-3">
                <p className="text-xs font-semibold text-muted-foreground">Projects ({sel.projectData.length})</p>
                {sel.projectData.map((pd, idx) => {
                  const projectStatus = pd.status as ProjectStatus | undefined;
                  const isPending = projectStatus === 'pending';
                  const statusConfig: Record<string, { color: string; label: string }> = {
                    pending: { color: '#ca8a04', label: 'Pending' },
                    approved: { color: '#16a34a', label: 'Approved' },
                    rejected: { color: '#dc2626', label: 'Rejected' },
                  };
                  const status = projectStatus ? statusConfig[projectStatus] : null;

                  return (
                    <Card key={idx} className={`p-4${isPending ? ' border-l-2 border-l-yellow-500' : ''}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs text-muted-foreground mb-1">Project {idx + 1}</p>
                          <p className="text-sm font-medium">{pd.title}</p>
                          <p className="text-sm text-muted-foreground mt-1">{pd.description}</p>
                          {pd.submittedAt && (
                            <p className="text-[11px] text-muted-foreground mt-2">
                              Submitted {new Date(pd.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </p>
                          )}
                        </div>
                        {status && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0"
                            style={{ backgroundColor: `${status.color}15`, color: status.color }}
                          >
                            {status.label}
                          </span>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Event */}
            {sel.event && (
              <Card className="p-4">
                <p className="text-xs font-semibold text-muted-foreground mb-2">Event</p>
                <p className="text-sm font-medium">{sel.event.name}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {sel.event.location} &middot;{' '}
                  {formatEventDateRange(sel.event.dateStart, sel.event.dateEnd)}
                </p>
              </Card>
            )}

            {/* Review Info (for already-reviewed) */}
            {sel.reviewedAt && (
              <Card className="p-4 space-y-2">
                <p className="text-xs font-semibold text-muted-foreground mb-2">Review</p>
                <DetailRow icon={<Calendar className="h-3.5 w-3.5" />} label="Reviewed" value={new Date(sel.reviewedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} />
                {sel.reviewer && <DetailRow label="Reviewer" value={displayName(sel.reviewer)} />}
                {sel.rejectionReason && (
                  <div className="mt-2 p-2 rounded bg-red-50 dark:bg-red-950 text-sm">
                    <p className="text-xs font-medium text-red-800 dark:text-red-200">Rejection Reason</p>
                    <p className="text-red-700 dark:text-red-300 mt-1">{sel.rejectionReason}</p>
                  </div>
                )}
                {sel.adminMessage && (
                  <div className="mt-2 p-2 rounded bg-blue-50 dark:bg-blue-950 text-sm">
                    <p className="text-xs font-medium text-blue-800 dark:text-blue-200">Info Request</p>
                    <p className="text-blue-700 dark:text-blue-300 mt-1">{sel.adminMessage}</p>
                  </div>
                )}
                {sel.adminNotes && (
                  <div className="mt-2 p-2 rounded bg-muted text-sm">
                    <p className="text-xs font-medium text-muted-foreground">Admin Notes</p>
                    <p className="mt-1">{sel.adminNotes}</p>
                  </div>
                )}
              </Card>
            )}

            {/* Submitted Date */}
            <Card className="p-4">
              <p className="text-xs font-medium text-muted-foreground mb-1">Submitted</p>
              <p className="text-sm">{new Date(sel.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</p>
            </Card>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground p-8 pt-24">
            <div className="text-center">
              <ClipboardCheck className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">Select a registration</p>
              <p className="text-sm mt-1">Click any registration to review details</p>
            </div>
          </div>
        )}
      </ScrollArea>
    </div>
  );

  return (
    <DesktopShell>
      <div className="h-full flex flex-col">
        {/* Page Title Bar */}
        <div className="px-6 py-4 border-b border-border bg-background flex-shrink-0">
          <h1 className="text-2xl font-bold">Network</h1>
        </div>

        <div className="flex-1 overflow-hidden">
          <ThreePanelLayout
            left={FiltersPanel}
            center={CommunityPanel}
            right={DetailsPanel}
            leftWidth="260px"
            equalCenterRight
            connectedPanels={!!sel}
          />
        </div>
      </div>

      {/* New Registration Dialog */}
      <Dialog open={newRegDialog} onOpenChange={setNewRegDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Registration</DialogTitle>
            <DialogDescription>Create a new attendee registration.</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-medium">First Name *</Label><Input value={newRegForm.firstName} onChange={(e) => setNewRegForm((f) => ({ ...f, firstName: e.target.value }))} placeholder="First name" className="h-9" /></div>
              <div><Label className="text-xs font-medium">Last Name *</Label><Input value={newRegForm.lastName} onChange={(e) => setNewRegForm((f) => ({ ...f, lastName: e.target.value }))} placeholder="Last name" className="h-9" /></div>
            </div>
            <div><Label className="text-xs font-medium">Email *</Label><Input type="email" value={newRegForm.email} onChange={(e) => setNewRegForm((f) => ({ ...f, email: e.target.value }))} placeholder="email@example.com" className="h-9" /></div>
            <div><Label className="text-xs font-medium">Participant Type</Label>
              <Select value={newRegForm.participantType} onValueChange={(v) => setNewRegForm((f) => ({ ...f, participantType: v }))}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Select type..." /></SelectTrigger>
                <SelectContent>{ATTENDEE_PARTICIPANT_TYPES.map(({ value, label }) => (<SelectItem key={value} value={value}>{label}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <MilitaryFields
              branchOfService={newRegForm.branchOfService}
              rank={newRegForm.rank}
              isRetired={newRegForm.isRetired}
              onBranchChange={(v) => setNewRegForm((f) => ({ ...f, branchOfService: v, rank: '' }))}
              onRankChange={(v) => setNewRegForm((f) => ({ ...f, rank: v }))}
              onRetiredChange={(v) => setNewRegForm((f) => ({ ...f, isRetired: v }))}
            />
            <div><Label className="text-xs font-medium">Organization *</Label><Input value={newRegForm.organization} onChange={(e) => setNewRegForm((f) => ({ ...f, organization: e.target.value }))} placeholder="Organization name" className="h-9" /></div>
            <div><Label className="text-xs font-medium">Job Position *</Label><Input value={newRegForm.role} onChange={(e) => setNewRegForm((f) => ({ ...f, role: e.target.value }))} placeholder="Job title or position" className="h-9" /></div>
            <div><Label className="text-xs font-medium">LinkedIn</Label><Input value={newRegForm.linkedinUrl} onChange={(e) => setNewRegForm((f) => ({ ...f, linkedinUrl: e.target.value }))} placeholder="https://linkedin.com/in/..." className="h-9" /></div>
            <div><Label className="text-xs font-medium">Website</Label><Input value={newRegForm.websiteUrl} onChange={(e) => setNewRegForm((f) => ({ ...f, websiteUrl: e.target.value }))} placeholder="https://..." className="h-9" /></div>
            <div><Label className="text-xs font-medium">Bio</Label><Textarea value={newRegForm.bio} onChange={(e) => setNewRegForm((f) => ({ ...f, bio: e.target.value }))} rows={2} maxLength={300} placeholder="Short bio (max 300 characters)" /><p className="text-[10px] text-muted-foreground text-right">{newRegForm.bio.length}/300</p></div>
            <div>
              <Label className="text-xs font-medium mb-1 block">Technology Interests (max 5)</Label>
              <div className="grid grid-cols-3 gap-1">
                {TECHNOLOGY_AREAS.map(({ value, label }) => {
                  const sel = newRegForm.accelerationInterests.includes(value);
                  const dis = !sel && newRegForm.accelerationInterests.length >= 5;
                  return (
                    <label key={value} className={`flex items-center gap-1 px-1.5 py-1 rounded border text-[11px] cursor-pointer transition-colors ${sel ? 'border-primary bg-primary/5 font-medium' : dis ? 'opacity-40 cursor-not-allowed border-border' : 'border-border hover:border-primary/50'}`}>
                      <Checkbox checked={sel} disabled={dis} onCheckedChange={() => { if (dis) return; setNewRegForm((f) => ({ ...f, accelerationInterests: sel ? f.accelerationInterests.filter((v) => v !== value) : [...f.accelerationInterests, value] })); }} className="h-3 w-3" />
                      <span className="truncate">{label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewRegDialog(false)} disabled={newRegLoading}>Cancel</Button>
            <Button onClick={handleNewRegistration} disabled={newRegLoading}>
              {newRegLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Create Registration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Acknowledgements follow-up reminder (after admin-created registration) */}
      <Dialog open={!!ackReminder} onOpenChange={(open) => { if (!open) setAckReminder(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Follow up on acknowledgements
            </DialogTitle>
            <DialogDescription>
              You created this registration on the attendee's behalf, so they have <strong>not</strong> yet
              accepted the required acknowledgements. Please email {ackReminder?.name || 'them'} to confirm
              they accept before the event. This registration is flagged <strong>Acks pending</strong> in the list until resolved.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border bg-muted/40 p-3 text-xs space-y-2 max-h-56 overflow-y-auto">
            {ATTENDEE_ACKNOWLEDGEMENTS.map((ack, i) => (
              <p key={i} className="leading-snug">• {ack}</p>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAckReminder(null)}>Close</Button>
            {ackReminder && (
              <Button asChild>
                <a
                  href={`mailto:${encodeURIComponent(ackReminder.email)}?subject=${encodeURIComponent('Action needed: confirm your Converge registration acknowledgements')}&body=${encodeURIComponent(
                    `Hi ${ackReminder.name || 'there'},\n\nWe've registered you for Converge@NPS. Before the event, please confirm you accept the following acknowledgements by replying to this email:\n\n${ATTENDEE_ACKNOWLEDGEMENTS.map((a) => `- ${a}`).join('\n\n')}\n\nThank you,\nConverge@NPS Team`
                  )}`}
                  onClick={() => setAckReminder(null)}
                >
                  <Mail className="h-4 w-4 mr-2" /> Email attendee
                </a>
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Registration Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Registration</DialogTitle>
            <DialogDescription>Update registration details.</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-medium">First Name</Label><Input value={editForm.firstName} onChange={(e) => setEditForm((f) => ({ ...f, firstName: e.target.value }))} className="h-9" /></div>
              <div><Label className="text-xs font-medium">Last Name</Label><Input value={editForm.lastName} onChange={(e) => setEditForm((f) => ({ ...f, lastName: e.target.value }))} className="h-9" /></div>
            </div>
            <div><Label className="text-xs font-medium">Email</Label><Input value={editForm.email} disabled className="h-9 bg-muted" /></div>
            <div><Label className="text-xs font-medium">Participant Type</Label>
              <Select value={editForm.participantType} onValueChange={(v) => setEditForm((f) => ({ ...f, participantType: v }))}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Select type..." /></SelectTrigger>
                <SelectContent>{ATTENDEE_PARTICIPANT_TYPES.map(({ value, label }) => (<SelectItem key={value} value={value}>{label}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <MilitaryFields
              branchOfService={editForm.branchOfService}
              rank={editForm.rank}
              isRetired={editForm.isRetired}
              onBranchChange={(v) => setEditForm((f) => ({ ...f, branchOfService: v, rank: '' }))}
              onRankChange={(v) => setEditForm((f) => ({ ...f, rank: v }))}
              onRetiredChange={(v) => setEditForm((f) => ({ ...f, isRetired: v }))}
            />
            <div><Label className="text-xs font-medium">Organization</Label><Input value={editForm.organization} onChange={(e) => setEditForm((f) => ({ ...f, organization: e.target.value }))} className="h-9" /></div>
            <div><Label className="text-xs font-medium">Job Position</Label><Input value={editForm.role} onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value }))} className="h-9" /></div>
            <div><Label className="text-xs font-medium">LinkedIn</Label><Input value={editForm.linkedinUrl} onChange={(e) => setEditForm((f) => ({ ...f, linkedinUrl: e.target.value }))} placeholder="https://linkedin.com/in/..." className="h-9" /></div>
            <div><Label className="text-xs font-medium">Website</Label><Input value={editForm.websiteUrl} onChange={(e) => setEditForm((f) => ({ ...f, websiteUrl: e.target.value }))} placeholder="https://..." className="h-9" /></div>
            <div><Label className="text-xs font-medium">Bio</Label><Textarea value={editForm.bio} onChange={(e) => setEditForm((f) => ({ ...f, bio: e.target.value }))} rows={2} maxLength={300} /><p className="text-[10px] text-muted-foreground text-right">{editForm.bio.length}/300</p></div>
            <div>
              <Label className="text-xs font-medium mb-1 block">Technology Interests (max 5)</Label>
              <div className="grid grid-cols-3 gap-1">
                {TECHNOLOGY_AREAS.map(({ value, label }) => {
                  const sel = editForm.accelerationInterests.includes(value);
                  const dis = !sel && editForm.accelerationInterests.length >= 5;
                  return (
                    <label key={value} className={`flex items-center gap-1 px-1.5 py-1 rounded border text-[11px] cursor-pointer transition-colors ${sel ? 'border-primary bg-primary/5 font-medium' : dis ? 'opacity-40 cursor-not-allowed border-border' : 'border-border hover:border-primary/50'}`}>
                      <Checkbox checked={sel} disabled={dis} onCheckedChange={() => { if (dis) return; setEditForm((f) => ({ ...f, accelerationInterests: sel ? f.accelerationInterests.filter((v) => v !== value) : [...f.accelerationInterests, value] })); }} className="h-3 w-3" />
                      <span className="truncate">{label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
          {/* Admin Role Assignment */}
          {editProfileId && (
            <>
              <Separator />
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Admin Roles</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={editRoles.isAdmin}
                      onCheckedChange={(checked) => setEditRoles((r) => ({ ...r, isAdmin: !!checked }))}
                      className="h-4 w-4"
                    />
                    <span className="text-sm font-medium">Admin</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={editRoles.isStaff}
                      onCheckedChange={(checked) => setEditRoles((r) => ({ ...r, isStaff: !!checked }))}
                      className="h-4 w-4"
                    />
                    <span className="text-sm font-medium">Staff</span>
                  </label>
                </div>
                {!editRoles.isAdmin && editRolesOriginal.isAdmin && (
                  <p className="text-[11px] text-destructive mt-1">Warning: This will remove admin access.</p>
                )}
              </div>
            </>
          )}
          {!editProfileId && (
            <p className="text-xs text-muted-foreground italic">Role assignment available after registration is approved.</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)} disabled={editSaving}>Cancel</Button>
            <Button onClick={handleEditSave} disabled={editSaving}>
              {editSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={approveDialog} onOpenChange={setApproveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Approve {selectedReg ? displayName(selectedReg) : ''}?</DialogTitle>
            <DialogDescription>
              This creates their account and sends a password setup email.
              {selectedReg?.registrationType === 'exhibitor' && (
                <span className="block mt-2 text-xs">
                  Note: this approves the point of contact as an attendee only. Approving the organization as an exhibitor is a separate decision on the Partners page.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialog(false)} disabled={actionLoading}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleApprove} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onOpenChange={(open) => { setRejectDialog(open); if (!open) setPreservePoc(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject {selectedReg ? displayName(selectedReg) : ''}?</DialogTitle>
            <DialogDescription>
              This user will be hidden from public view.
            </DialogDescription>
          </DialogHeader>
          {selectedReg?.registrationType === 'exhibitor' && selectedReg?.status === 'pending' && (
            <div className="flex items-start gap-3 py-3 px-1">
              <Checkbox
                id="preserve-poc"
                checked={preservePoc}
                onCheckedChange={(checked) => setPreservePoc(checked === true)}
              />
              <Label htmlFor="preserve-poc" className="text-sm leading-snug cursor-pointer">
                Approve the point of contact as a general attendee
                <span className="block text-xs text-muted-foreground mt-0.5">
                  The exhibitor application will be rejected, but {selectedReg.firstName} will receive an approval email and can attend as an individual.
                </span>
              </Label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(false)} disabled={actionLoading}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reinstate Dialog */}
      <Dialog open={reinstateDialog} onOpenChange={setReinstateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reinstate {selectedReg ? displayName(selectedReg) : ''}?</DialogTitle>
            <DialogDescription>
              This moves the registration back to pending review. You can then approve or reject it again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReinstateDialog(false)} disabled={actionLoading}>Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={handleReinstate} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RotateCcw className="h-4 w-4 mr-2" />}
              Reinstate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </DesktopShell>
  );
}

function DetailRow({ icon, label, value, link }: { icon?: React.ReactNode; label?: string; value: string; link?: boolean }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {icon && <span className="text-muted-foreground flex-shrink-0">{icon}</span>}
      {label && <span className="text-muted-foreground flex-shrink-0">{label}:</span>}
      {link ? (
        <a href={value} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate">{value}</a>
      ) : (
        <span className="truncate">{value}</span>
      )}
    </div>
  );
}
